
import React from 'react';
import { QuizDisplay } from "../QuizDisplay";
import { Skeleton } from "@/components/ui/skeleton";
import { ErrorContentViewer } from "./ErrorContentViewer";

interface QuizContentViewerProps {
  quiz: any;
  isLoading: boolean;
  isAccessible: boolean;
  progress: number;
  isLastSection?: boolean;
  quizId?: string;
  resourceId?: string;
  quizCompleted?: boolean;
  quizAttempt?: any;
  onComplete?: () => void;
  onQuizUpdate?: (quizId: string, attempt: any) => Promise<void>;
  error?: string | null;
}

export function QuizContentViewer({
  quiz,
  isLoading,
  isAccessible,
  progress,
  isLastSection = false,
  quizId,
  resourceId,
  quizCompleted,
  quizAttempt,
  onComplete,
  onQuizUpdate,
  error
}: QuizContentViewerProps) {
  // Debug logging to help identify the issue
  console.log('QuizContentViewer props:', {
    hasQuiz: !!quiz,
    quizData: quiz,
    isLoading,
    isAccessible,
    quizId,
    resourceId,
    quizCompleted,
    hasQuizAttempt: !!quizAttempt,
    quizAttemptScore: quizAttempt?.score,
    quizAttemptPassed: quizAttempt?.passed,
    error
  });

  // Show loading state
  if (isLoading) {
    return (
      <div className="w-full max-w-none px-6">
        <div className="space-y-4">
          <Skeleton className="h-12 w-3/4" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-10 w-1/2" />
        </div>
      </div>
    );
  }

  // Show error if no quiz is available and we're not loading
  if ((!quiz && !isLoading) || error) {
    return (
      <ErrorContentViewer
        message="Quiz data not available" 
        details={error || (!resourceId 
          ? "No resource ID provided to load quiz" 
          : "The quiz data couldn't be loaded. Please try refreshing the page.")}
        onRetry={() => window.location.reload()}
      />
    );
  }

  return (
    <div className="w-full max-w-none px-6">
      <QuizDisplay 
        quiz={quiz} 
        isLoading={isLoading}
        isAccessible={isAccessible}
        progress={progress}
        isLastSection={isLastSection}
        quizId={quizId || quiz?.id}
        resourceId={resourceId}
        quizAttempt={quizAttempt}
        quizCompleted={quizCompleted}
        onComplete={onComplete}
        onQuizUpdate={onQuizUpdate}
      />
    </div>
  );
}
